import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from 'react-router-dom';

import Layout from "~/components/UI/Layout/Layout";
import List from "./components/List";
import DrawerIRPF from "~/containers/DrawerIRPF";
import { loadIRPFs, changeYear } from "./actions";
import { LoadingTopBar } from "~/components/UI/LoadingTopBar";
import { LocalCacheHandler } from '~/utils/local-cache-handler';
import { CACHE_ACTIONS } from '~/constants/cache-actions.constants';
import usePersistedFilters from '~/_shared/hooks/usePersistedFilters';
import {
  IRPF_FILTERS_KEY,
  getDefaultIrpfFilters,
  normalizeIrpfFilters,
  irpfYearToMoment,
} from './helpers';

export const ScreensIRPF = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const isFirstLoad = useRef(true);

  // Filters kept across refreshes for a few minutes (per tenant/user/page).
  const persisted = usePersistedFilters(IRPF_FILTERS_KEY, getDefaultIrpfFilters);
  const applied = normalizeIrpfFilters(persisted.filters, persisted.defaults);

  // Loads the restored (or default) year on mount and whenever it changes,
  // keeping redux current_year (read by the list and DrawerIRPF) in sync.
  useEffect(() => {
    dispatch(changeYear(irpfYearToMoment(applied.year)));
    loadData(applied.year);
  }, [applied.year]);

  async function loadData(year) {
    const firstLoad = isFirstLoad.current;
    isFirstLoad.current = false;
    // The IndexedDB cache is keyed by route only, so it only ever holds the
    // default year; using it for another year would flash wrong data.
    const usesCache = year === persisted.defaults.year;

    try {
      if (firstLoad && usesCache) {
        await LocalCacheHandler.load({
          actionName: CACHE_ACTIONS.irpfs,
          dispatch,
          pathname
        });
      }
      const request = { year: year };
      // First load keeps the top bar only (as before); later changes use the default loader.
      if (firstLoad) request.showLoading = false;
      const { payload } = await dispatch(loadIRPFs(request));
      if (usesCache) {
        await LocalCacheHandler.save({
          pathname,
          data: payload.data.customers
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div>
      <LoadingTopBar isActive={isLoading} />
      <Layout>
        <DrawerIRPF />
        <List persisted={persisted} applied={applied} />
      </Layout>
    </div>
  );
};
